import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    ArrowRight, ArrowUpRight, Check, Loader2, Mail, MessageSquare, Phone, Play, Store,
} from "lucide-react";
import ProductChrome from "@/components/product/ProductChrome";
import StoreMock from "@/components/product/StoreMock";
import CallPicker from "@/components/product/CallPicker";
import { useMall } from "@/context/MallContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const GREEN = "#2563EB";
const INK_GREEN = "#1E40AF";
const BLUE = "#1a56db";

const Eyebrow = ({ children, testid, color = GREEN }) => (
    <p className="flex items-center gap-2.5 font-mono text-[11px] font-bold uppercase tracking-[0.24em] text-zinc-500"
        data-testid={testid}>
        <span className="h-[3px] w-7" style={{ background: color }} />{children}
    </p>
);

const Bullets = ({ rows, testid }) => (
    <dl className="mt-6 space-y-5" data-testid={testid}>
        {rows.map(([head, body]) => (
            <div key={head} className="border-l-2 border-zinc-200 pl-4">
                <dt className="font-display text-[15px] font-extrabold tracking-tight text-zinc-950">{head}</dt>
                <dd className="mt-1 text-[14px] font-light leading-relaxed text-zinc-600">{body}</dd>
            </div>
        ))}
    </dl>
);

function Money({ rows }) {
    return (
        <div className="grid grid-cols-2 gap-x-8 gap-y-6 border-y border-zinc-200 py-7 sm:grid-cols-4"
            data-testid="product-money">
            {rows.map(({ value, label }, i) => (
                <div key={i} data-testid={`money-${i}`}>
                    <p className="font-display text-3xl font-black tracking-tight text-zinc-950 tabular-nums sm:text-4xl">
                        {value}
                    </p>
                    <p className="mt-1 text-[13px] font-light leading-snug text-zinc-600">{label}</p>
                </div>
            ))}
        </div>
    );
}

/** The spider-web line under every feature: where the customer sees it, where the owner runs it. */
function Web({ to, office, officeLabel, testid }) {
    return (
        <p className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-[13px]" data-testid={testid}>
            <Link to={to} className="inline-flex items-center gap-1 font-semibold text-zinc-950 underline decoration-2"
                style={{ textDecorationColor: GREEN }}>
                See it as a customer <ArrowUpRight size={12} />
            </Link>
            <Link to={office} className="inline-flex items-center gap-1 text-zinc-600 hover:text-zinc-950">
                You manage it at <span className="font-semibold text-zinc-800">Back office → {officeLabel}</span> <ArrowUpRight size={12} />
            </Link>
        </p>
    );
}

function Tier({ t, cycle, onStart, busy, accent }) {
    const yearly = cycle === "yearly" && t.price_year;
    const price = yearly ? t.price_year : t.price_month;
    return (
        <div data-testid={`tier-card-${t.id}`}
            className={`flex flex-col rounded-2xl border bg-white p-6 transition-transform duration-200 hover:-translate-y-0.5 ${t.keystone ? "border-zinc-950 bg-zinc-950 text-white" : "border-zinc-200"}`}>
            <div className="flex items-center gap-2">
                <p className={`font-display text-[15px] font-extrabold tracking-tight ${t.keystone ? "text-white" : "text-zinc-950"}`}>
                    {t.name}
                </p>
                {t.keystone && (
                    <span className="rounded-full px-2 py-0.5 font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-white"
                        style={{ background: GREEN }} data-testid="tier-keystone">private site</span>
                )}
            </div>
            <p className="mt-3 font-display text-4xl font-black tracking-tight">
                ${price.toLocaleString()}
                <span className={`text-sm font-bold ${t.keystone ? "text-zinc-400" : "text-zinc-500"}`}>
                    {yearly ? "/yr" : "/mo"}
                </span>
            </p>
            <p className={`mt-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.12em]`}
                style={{ color: t.keystone ? GREEN : INK_GREEN }} data-testid={`tier-offer-${t.id}`}>
                {yearly
                    ? `save ${t.save_pct}% · ${t.months_free} months free`
                    : `first month $${t.first_month.toFixed(2)} · 50% off`}
            </p>
            {t.what && (
                <p className={`mt-3 font-mono text-[11px] font-bold uppercase tracking-[0.14em] ${t.keystone ? "text-zinc-300" : "text-zinc-700"}`} data-testid={`tier-what-${t.id}`}>
                    {t.what}
                </p>
            )}
            <p className={`mt-2 text-[13px] font-light leading-snug ${t.keystone ? "text-zinc-300" : "text-zinc-600"}`}>
                {t.tagline}
            </p>
            <ul className="mt-5 flex-1 space-y-2.5">
                {t.perks.map((p) => (
                    <li key={p} className={`flex gap-2 text-[13px] font-light leading-snug ${t.keystone ? "text-zinc-200" : "text-zinc-700"}`}>
                        <Check size={14} className="mt-0.5 shrink-0" style={{ color: t.keystone ? GREEN : accent }} /> {p}
                    </li>
                ))}
            </ul>
            <button onClick={() => onStart(t, cycle)} disabled={busy} data-testid={`tier-start-${t.id}`}
                className={`mt-6 inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-[14px] font-bold transition-transform duration-200 hover:-translate-y-px disabled:opacity-60 text-white`}
                style={t.keystone ? { background: GREEN } : { background: "#09090b" }}>
                Start this plan <ArrowRight size={15} />
            </button>
            <p className={`mt-2.5 text-center font-mono text-[11px] uppercase tracking-[0.14em] ${t.keystone ? "text-zinc-400" : "text-zinc-500"}`} data-testid={`tier-fee-${t.id}`}>
                {t.fee === false ? "Cancel any time · 0% of sales" : "Cancel any time · 1% of sales"}
            </p>
        </div>
    );
}

/** A real shop that runs on this software — local customers order there, print shops see a customer's site in the wild. */
function LocalShopAd({ local, band }) {
    if (!local?.live) return null;
    return (
        <div className="mb-5 overflow-hidden rounded-2xl border border-zinc-200 bg-white" data-testid="hero-local-ad">
            <div className="h-1.5" style={{ background: GREEN }} />
            <div className="p-5">
                <p className="font-mono text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: INK_GREEN }} data-testid="hero-local-ad-kicker">
                    A real shop on this software · {local.city}
                </p>
                <h3 className="mt-2 font-display text-xl font-extrabold leading-tight tracking-tight text-zinc-950" data-testid="hero-local-ad-head">
                    Shop a local sign shop that runs on this white-label website.
                </h3>
                <p className="mt-2 text-[14px] font-light leading-relaxed text-zinc-600" data-testid="hero-local-ad-body">
                    {local.name} takes real orders on the exact site you are looking at — same designers, same checkout.
                    Need a sign or shirts printed in {local.city}? Order from them. Thinking of renting? This is what a customer's shop looks like live.
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-3">
                    <Link to={local.url} data-testid="hero-local-ad-cta"
                        className="inline-flex items-center gap-2 rounded-full bg-zinc-950 px-5 py-2.5 text-[14px] font-bold text-white transition-transform duration-200 hover:-translate-y-px">
                        <Store size={14} /> Order from {local.name} <ArrowUpRight size={14} />
                    </Link>
                    <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-zinc-500">{band || "pickup in Brooklyn or shipped"}</span>
                </div>
            </div>
        </div>
    );
}

export default function Product() {
    const [d, setD] = useState(null);
    const [busy, setBusy] = useState(false);
    const [cycle, setCycle] = useState("monthly");
    const { enterDemo } = useMall();
    const { user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => { api.get("/pitch").then(({ data }) => setD(data)).catch(() => setD(false)); }, []);

    const seeItLive = () => {
        const slug = d?.demo?.slug;
        if (!slug) return;
        enterDemo(slug);
        navigate("/");
    };

    const startPlan = (t, c) => {
        setBusy(true);
        const to = `/account/sell?tier=${t.id}&cycle=${c || "monthly"}`;
        navigate(user ? to : `/register?next=${encodeURIComponent(to)}`);
    };

    if (d === null) return (
        <ProductChrome>
            <div className="flex min-h-[70vh] items-center justify-center" data-testid="product-loading">
                <Loader2 className="animate-spin text-zinc-400" />
            </div>
        </ProductChrome>
    );

    const groups = d?.groups || [];
    const off = d?.first_month_off ?? 50;
    const ICONS = [Phone, MessageSquare, Mail, MessageSquare];

    return (
        <ProductChrome local={d?.local}>
            {/* ---------------- hero ---------------- */}
            <section className="relative overflow-hidden" data-testid="product-hero">
                <div className="pointer-events-none absolute -right-40 -top-40 h-[32rem] w-[32rem] rounded-full opacity-[0.13]"
                    style={{ background: `radial-gradient(circle, ${GREEN} 0%, transparent 65%)` }} />
                <div className="mx-auto grid max-w-6xl gap-14 px-5 pb-16 pt-14 sm:px-8 lg:grid-cols-[1.05fr_.95fr] lg:items-center lg:pb-24 lg:pt-20">
                    <div>
                        <Eyebrow testid="hero-eyebrow">Rent the whole print shop</Eyebrow>
                        <h1 className="mt-5 font-display text-4xl font-black leading-[0.95] tracking-[-0.03em] text-zinc-950 sm:text-5xl lg:text-6xl"
                            data-testid="hero-headline">
                            {d?.headline || "White-label print shop websites."}
                        </h1>
                        <p className="mt-5 max-w-xl text-lg font-light leading-relaxed text-zinc-600" data-testid="hero-subhead">
                            {d?.subhead}
                        </p>

                        <ul className="mt-7 space-y-2.5" data-testid="hero-beats">
                            {(d?.beats || []).map((b) => (
                                <li key={b} className="flex items-start gap-2.5 text-[15px] font-medium text-zinc-800">
                                    <Check size={16} className="mt-1 shrink-0" style={{ color: INK_GREEN }} /> {b}
                                </li>
                            ))}
                        </ul>

                        <div className="mt-9 flex flex-wrap items-center gap-3">
                            <a href="#pricing" data-testid="hero-see-pricing"
                                className="inline-flex items-center gap-2 rounded-full bg-zinc-950 px-7 py-3.5 text-[15px] font-bold text-white transition-transform duration-200 hover:-translate-y-px">
                                See the 4 plans <ArrowRight size={15} />
                            </a>
                            <button onClick={seeItLive} data-testid="hero-see-it-live"
                                className="inline-flex items-center gap-2 rounded-full border border-zinc-300 bg-white px-6 py-3.5 text-[15px] font-bold text-zinc-950 transition-colors hover:border-zinc-950">
                                <Play size={14} /> See a demo
                            </button>
                        </div>
                        <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.14em] text-zinc-500">
                            ${(d?.tiers?.[0]?.price_month ?? 10)}–${(d?.tiers?.[d.tiers.length - 1]?.price_month ?? 299)} a month ·
                            {" "}{off}% off month one · {d?.fee_pct ?? 1}% of sales · your own Stripe
                        </p>
                    </div>

                    <div className="lg:pl-4">
                        <LocalShopAd local={d?.local} band="order online · pick up or ship" />
                        <StoreMock shots={d?.demo?.shots} color={GREEN} />
                        <p className="mt-3 text-center font-mono text-[11px] uppercase tracking-[0.16em] text-zinc-500">
                            your shop · your prices · your Stripe
                        </p>
                    </div>
                </div>
            </section>

            {/* ---------------- how you make money ---------------- */}
            <section className="mx-auto max-w-6xl px-5 sm:px-8">
                <Eyebrow testid="money-eyebrow">How you make money</Eyebrow>
                <div className="mt-4">
                    <Money rows={d?.money || []} />
                </div>
                <p className="py-6 text-[15px] font-light text-zinc-600" data-testid="product-trust-line">
                    <span className="font-semibold text-zinc-950">{d?.proof_line?.question}</span>{" "}
                    {d?.proof_line?.answer}{" "}
                    {d?.proof_line?.live && (
                        <Link to={d.proof_line.url} data-testid="trust-local-link"
                            className="inline-flex items-center gap-1 font-semibold text-zinc-950 underline decoration-2"
                            style={{ textDecorationColor: GREEN }}>
                            {d.proof_line.link_label} <ArrowUpRight size={13} />
                        </Link>
                    )}
                </p>
            </section>

            {/* ---------------- what you get ---------------- */}
            <section id="what-you-get" className="mx-auto max-w-6xl scroll-mt-20 px-5 py-16 sm:px-8 lg:py-24"
                data-testid="product-gets">
                <Eyebrow>What you get</Eyebrow>
                <h2 className="mt-4 max-w-3xl font-display text-3xl font-black tracking-[-0.02em] text-zinc-950 sm:text-4xl lg:text-5xl">
                    A whole print shop website, with your name on it.
                </h2>
                <p className="mt-4 max-w-2xl text-[15px] font-light leading-relaxed text-zinc-600" data-testid="gets-intro">
                    Your customers see five menu items in the header: Shirts, Signs, Classifieds,
                    Classes and Wholesale. Each one is explained below. Each one has a page your customer
                    shops on and a page in your back office where you run it, with the same name in both places.
                </p>
                <div className="mt-12 grid gap-x-14 gap-y-11 sm:grid-cols-2">
                    {(d?.gets || []).map((g, i) => (
                        <div key={g.key} data-testid={`get-${g.key}`} id={`get-${g.key}`} className="scroll-mt-24">
                            <p className="font-mono text-[11px] font-bold tracking-[0.2em]" style={{ color: INK_GREEN }}>
                                {g.num || String(i + 1).padStart(2, "0")} · {g.meta}
                            </p>
                            <h3 className="mt-2 font-display text-xl font-extrabold tracking-tight text-zinc-950">{g.head}</h3>
                            <p className="mt-2 text-[15px] font-light leading-relaxed text-zinc-600">{g.body}</p>
                            <Web to={g.to} office={g.office} officeLabel={g.office_label} testid={`get-web-${g.key}`} />
                        </div>
                    ))}
                </div>
            </section>

            {/* ---------------- the two modes ---------------- */}
            <section id="modes" className="scroll-mt-20 border-y border-zinc-200 bg-zinc-50" data-testid="product-modes">
                <div className="mx-auto max-w-6xl px-5 py-16 sm:px-8 lg:py-24">
                    <Eyebrow testid="modes-eyebrow">Two switches that make you money on day one</Eyebrow>
                    <p className="mt-4 max-w-2xl text-[15px] font-light leading-relaxed text-zinc-600" data-testid="modes-intro">
                        Wizard Mode gets your shop open. Consignment Mode decides what you print yourself and
                        what 13thav prints for you. Both live in your back office.
                    </p>
                    <div className="mt-10 grid gap-14 lg:grid-cols-2">
                        {(d?.modes || []).map((m) => (
                            <div key={m.id} data-testid={`mode-${m.id}`}>
                                <Eyebrow color={m.id === "wizard" ? BLUE : GREEN}>{m.kicker}</Eyebrow>
                                <h2 className="mt-4 font-display text-2xl font-black leading-tight tracking-[-0.02em] text-zinc-950 sm:text-3xl">
                                    {m.head}
                                </h2>
                                <p className="mt-3 text-[15px] font-light leading-relaxed text-zinc-600">{m.body}</p>
                                <Bullets rows={m.bullets} testid={`mode-${m.id}-bullets`} />
                                <p className="mt-5 text-[14px] font-semibold text-zinc-950">{m.foot}</p>
                                {m.office && (
                                    <Link to={m.office} data-testid={`mode-${m.id}-office`}
                                        className="mt-3 inline-flex items-center gap-1 text-[13px] text-zinc-600 hover:text-zinc-950">
                                        You find it at <span className="font-semibold text-zinc-800">Back office → {m.office_label}</span> <ArrowUpRight size={12} />
                                    </Link>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ---------------- see it live ---------------- */}
            <section id="see-it-live" className="scroll-mt-20 bg-white" data-testid="product-demo-band">
                <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-x-10 gap-y-5 px-5 py-14 sm:px-8">
                    <div className="min-w-[18rem] flex-1">
                        <Eyebrow>{d?.demo_band?.kicker}</Eyebrow>
                        <h2 className="mt-4 font-display text-3xl font-black tracking-[-0.02em] text-zinc-950 sm:text-4xl" data-testid="demo-band-head">
                            {d?.demo_band?.head}
                        </h2>
                        <p className="mt-3 max-w-xl text-[15px] font-light leading-relaxed text-zinc-600">
                            {d?.demo_band?.body}
                        </p>
                        {d?.local?.live && (
                            <p className="mt-3 max-w-xl text-[15px] font-light leading-relaxed text-zinc-600" data-testid="demo-band-local">
                                {d?.demo_band?.local}{" "}
                                <Link to={d.local.url} className="font-semibold text-zinc-950 underline decoration-2" style={{ textDecorationColor: GREEN }}>
                                    Shop {d.local.name}
                                </Link>
                            </p>
                        )}
                    </div>
                    <div className="flex flex-wrap gap-3">
                        <button onClick={seeItLive} data-testid="demo-band-cta"
                            className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-[15px] font-bold text-white transition-transform duration-200 hover:-translate-y-px"
                            style={{ background: GREEN }}>
                            <Play size={14} /> See a demo
                        </button>
                        <Link to="/directory" data-testid="product-browse-directory"
                            className="inline-flex items-center gap-1.5 self-center text-[14px] font-medium text-zinc-600 underline decoration-zinc-300 hover:text-zinc-950">
                            Browse the directory <ArrowUpRight size={13} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* ---------------- the directory half of the business ---------------- */}
            <section id="directory" className="scroll-mt-20 border-y border-zinc-200 bg-zinc-950"
                data-testid="product-directory">
                <div className="mx-auto grid max-w-6xl gap-10 px-5 py-16 sm:px-8 lg:grid-cols-[1.1fr_.9fr] lg:py-20">
                    <div>
                        <p className="flex items-center gap-2.5 font-mono text-[11px] font-bold uppercase tracking-[0.24em] text-zinc-500">
                            <span className="h-[3px] w-7" style={{ background: GREEN }} />{d?.directory?.kicker}
                        </p>
                        <h2 className="mt-4 font-display text-3xl font-black leading-tight tracking-[-0.02em] text-white sm:text-4xl">
                            {d?.directory?.head}
                        </h2>
                        <p className="mt-4 max-w-xl text-[15px] font-light leading-relaxed text-zinc-400">
                            {d?.directory?.body}
                        </p>
                    </div>
                    <ul className="space-y-3.5 self-center">
                        {(d?.directory?.bullets || []).map((b) => (
                            <li key={b} className="flex gap-2.5 text-[14px] font-light leading-relaxed text-zinc-300">
                                <Check size={15} className="mt-0.5 shrink-0" style={{ color: GREEN }} /> {b}
                            </li>
                        ))}
                        <li>
                            <Link to="/directory" data-testid="directory-visit"
                                className="mt-2 inline-flex items-center gap-1.5 text-[14px] font-bold text-white underline decoration-2"
                                style={{ textDecorationColor: GREEN }}>
                                Browse the directory <ArrowUpRight size={14} />
                            </Link>
                        </li>
                    </ul>
                </div>
            </section>

            {/* ---------------- pricing ---------------- */}
            <section id="pricing" className="mx-auto max-w-6xl scroll-mt-20 px-5 py-16 sm:px-8 lg:py-24"
                data-testid="product-pricing">
                <Eyebrow>Pricing · four plans</Eyebrow>
                <h2 className="mt-4 max-w-2xl font-display text-3xl font-black tracking-[-0.02em] text-zinc-950 sm:text-4xl lg:text-5xl" data-testid="pricing-head">
                    Three plans list your shop in the directory. One plan is a private website of your own.
                </h2>
                {/* The billing switch rides along while you scroll both plan groups. */}
                <div className="pointer-events-none sticky top-[72px] z-30 mt-6 flex justify-end">
                    <div className="pointer-events-auto inline-flex rounded-full border border-zinc-300 bg-white/95 p-1 shadow-sm backdrop-blur" data-testid="pricing-cycle">
                        {[["monthly", "Monthly"], ["yearly", "Yearly · save up to 45%"]].map(([k, label]) => (
                            <button key={k} onClick={() => setCycle(k)} data-testid={`pricing-cycle-${k}`}
                                className={`rounded-full px-4 py-2 text-[13px] font-bold transition-colors ${cycle === k ? "bg-zinc-950 text-white" : "text-zinc-600 hover:text-zinc-950"}`}>
                                {label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="mt-10 space-y-8" data-testid="tier-grid">
                    {groups.map((g) => {
                        const mall = g.id === "directory";
                        const accent = mall ? BLUE : INK_GREEN;
                        return (
                            <div key={g.id} data-testid={`pricing-group-${g.id}`}
                                className="rounded-3xl border-2 p-5 sm:p-7"
                                style={{ borderColor: mall ? "#c7d7fb" : "#d4d4d8", background: mall ? "#f7faff" : "#fafafa" }}>
                                <div className="flex flex-wrap items-end justify-between gap-x-8 gap-y-3">
                                    <div className="max-w-2xl">
                                        <p className="font-mono text-[11px] font-bold uppercase tracking-[0.2em]"
                                            style={{ color: accent }}>{g.kicker}</p>
                                        <h3 className="mt-2 font-display text-xl font-extrabold tracking-tight text-zinc-950 sm:text-2xl">
                                            {g.label}
                                        </h3>
                                        <p className="mt-2 text-[14px] font-light leading-relaxed text-zinc-600">{g.blurb}</p>
                                    </div>
                                    <p className="max-w-xs text-[13px] font-medium" style={{ color: accent }}>{g.note}</p>
                                </div>
                                <div className={`mt-6 grid gap-4 ${mall ? "sm:grid-cols-2 lg:grid-cols-3" : "lg:max-w-md"}`}>
                                    {g.tiers.map((t) => (
                                        <Tier key={t.id} t={t} cycle={cycle} onStart={startPlan} busy={busy} accent={accent} />
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="mt-12 grid gap-x-14 gap-y-7 sm:grid-cols-2" data-testid="product-terms">
                    {(d?.terms || []).map(([head, body]) => (
                        <div key={head}>
                            <h3 className="font-display text-[15px] font-extrabold tracking-tight text-zinc-950">{head}</h3>
                            <p className="mt-1.5 text-[14px] font-light leading-relaxed text-zinc-600">{body}</p>
                        </div>
                    ))}
                </div>

                <p className="mt-9 text-[14px] font-light text-zinc-600" data-testid="pricing-demo-line">
                    Not sure yet?{" "}
                    <button onClick={seeItLive} data-testid="pricing-see-in-action"
                        className="font-semibold text-zinc-950 underline decoration-2" style={{ textDecorationColor: GREEN }}>
                        Try the demo shop
                    </button>{" "}
                    or <Link to="/directory" className="font-semibold text-zinc-950 underline decoration-zinc-300">browse the directory</Link>{" "}
                    to see the shops that are already open.
                </p>
            </section>

            {/* ---------------- help ---------------- */}
            <section className="border-y border-zinc-200 bg-zinc-50" data-testid="product-support">
                <div className="mx-auto grid max-w-6xl gap-10 px-5 py-14 sm:px-8 lg:grid-cols-[1fr_1fr]">
                    <div>
                        <Eyebrow>{d?.support?.kicker}</Eyebrow>
                        <h2 className="mt-4 font-display text-2xl font-black tracking-[-0.02em] text-zinc-950 sm:text-3xl">
                            {d?.support?.head}
                        </h2>
                        <p className="mt-3 max-w-lg text-[15px] font-light leading-relaxed text-zinc-600">
                            {d?.support?.body}
                        </p>
                        <a href={`tel:${d?.business?.phone_raw}`} data-testid="support-phone"
                            className="mt-5 inline-flex items-center gap-2 rounded-full bg-zinc-950 px-6 py-3 text-[15px] font-bold text-white transition-transform duration-200 hover:-translate-y-px">
                            <Phone size={14} /> {d?.business?.phone}
                        </a>
                    </div>
                    <div className="grid gap-4 self-center sm:grid-cols-2">
                        {(d?.support?.bullets || []).map(([head, body], i) => {
                            const Icon = ICONS[i] || MessageSquare;
                            return (
                                <div key={head} className="rounded-2xl border border-zinc-200 bg-white p-4">
                                    <Icon size={15} style={{ color: INK_GREEN }} />
                                    <p className="mt-2 font-display text-[14px] font-extrabold tracking-tight text-zinc-950">{head}</p>
                                    <p className="mt-1 text-[13px] font-light leading-snug text-zinc-600">{body}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ---------------- quote ---------------- */}
            <section className="bg-white" data-testid="product-quote">
                <div className="mx-auto max-w-4xl px-5 py-16 sm:px-8">
                    <p className="font-display text-2xl font-extrabold leading-snug tracking-[-0.015em] text-zinc-950 sm:text-3xl">
                        <span style={{ color: INK_GREEN }}>“</span>{d?.quote?.body}<span style={{ color: INK_GREEN }}>”</span>
                    </p>
                    <p className="mt-6 text-[14px] font-semibold text-zinc-950">{d?.quote?.who}</p>
                    <p className="text-[13px] text-zinc-500">{d?.quote?.where}</p>
                </div>
            </section>

            {/* ---------------- book a setup call ---------------- */}
            <section id="book-a-call" className="scroll-mt-20 border-y border-zinc-200 bg-zinc-50"
                data-testid="product-call-band">
                <div className="mx-auto grid max-w-6xl gap-10 px-5 py-16 sm:px-8 lg:grid-cols-[.85fr_1.15fr] lg:py-20">
                    <div>
                        <Eyebrow>{d?.call?.kicker}</Eyebrow>
                        <h2 className="mt-4 font-display text-3xl font-black leading-tight tracking-[-0.02em] text-zinc-950 sm:text-4xl">
                            {d?.call?.head}
                        </h2>
                        <p className="mt-4 max-w-md text-[15px] font-light leading-relaxed text-zinc-600" data-testid="call-body">
                            {d?.call?.body}
                        </p>
                        <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.14em] text-zinc-500">
                            {d?.call?.note}
                        </p>
                    </div>
                    <div className="rounded-3xl border border-zinc-200 bg-white p-6 sm:p-7">
                        <CallPicker tier={cycle === "yearly" ? "yearly plans" : "monthly plans"} />
                    </div>
                </div>
            </section>

            {/* ---------------- last word ---------------- */}
            <section className="mx-auto max-w-6xl px-5 pb-8 sm:px-8" data-testid="product-final-cta">
                <div className="overflow-hidden rounded-3xl bg-zinc-950 px-7 py-14 sm:px-14">
                    <h2 className="max-w-2xl font-display text-3xl font-black leading-tight tracking-[-0.02em] text-white sm:text-4xl">
                        {d?.close?.head}
                    </h2>
                    <p className="mt-4 max-w-xl text-[15px] font-light leading-relaxed text-zinc-400">
                        {d?.close?.body}
                    </p>
                    <ul className="mt-8 grid gap-3 sm:grid-cols-2" data-testid="close-bullets">
                        {(d?.close?.bullets || []).map((b) => (
                            <li key={b} className="flex gap-2.5 text-[15px] font-medium leading-snug text-zinc-100">
                                <Check size={16} className="mt-0.5 shrink-0" style={{ color: GREEN }} /> {b}
                            </li>
                        ))}
                    </ul>
                    <div className="mt-9 flex flex-wrap items-center gap-3">
                        <a href="#pricing" data-testid="final-cta-pricing"
                            className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-[15px] font-bold text-white transition-transform duration-200 hover:-translate-y-px"
                            style={{ background: GREEN }}>
                            Start your shop <ArrowRight size={15} />
                        </a>
                        <button onClick={seeItLive} data-testid="final-cta-demo"
                            className="inline-flex items-center gap-2 rounded-full border border-white/25 px-6 py-3.5 text-[15px] font-bold text-white transition-colors hover:border-white">
                            <Play size={14} /> See a demo first
                        </button>
                        <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-zinc-500">
                            {d?.close?.reassure}
                        </p>
                    </div>
                </div>
            </section>

            {/* ---------------- local customers ---------------- */}
            {d?.local?.live && (
                <section className="mx-auto max-w-6xl px-5 pb-20 sm:px-8" data-testid="product-local-band">
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-3 rounded-2xl border border-zinc-200 bg-white px-6 py-5">
                        <Store size={17} className="text-zinc-950" />
                        <p className="text-[15px] font-light text-zinc-600" data-testid="local-band-body">
                            <span className="font-bold text-zinc-950">{d.local_band?.question}</span>{" "}
                            {d.local_band?.body}
                        </p>
                        <Link to={d.local.url} data-testid="product-local-cta"
                            className="ml-auto inline-flex items-center gap-2 rounded-full bg-zinc-950 px-5 py-2.5 text-[14px] font-bold text-white transition-transform duration-200 hover:-translate-y-px">
                            Shop {d.local.name} <ArrowUpRight size={14} />
                        </Link>
                    </div>
                </section>
            )}
        </ProductChrome>
    );
}
